import React, { useState, useRef, useEffect } from 'react';
import { openSnackbar } from 'api/snackbar';
import { fetchInvoiceByInvoiceNumber } from 'utils/apiHelper';

// material-ui
import { Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio, Button, Typography, Box, IconButton, Tooltip } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

// components
import InvoicePrintPreviewModal from 'components/print/InvoicePrintPreviewModal';
import PharmacyInvoiceThermalContent from '../pages/invoice/PharmacyInvoiceThermalContent';
import PharmacyInvoiceA4Content from '../pages/invoice/PharmacyInvoiceA4Content';
import LabInvoiceThermalContent from '../pages/invoice/LabInvoiceThermalContent';
import LabInvoiceA4Content from '../pages/invoice/LabInvoiceA4Content';
import { normalizeHospitalDetails, summarizeInclusiveGst, getItemGstPercentage } from 'utils/gst';
import { resolvePharmacyInvoiceTotals } from '../pages/invoice/utils';

const InvoiceViewer = ({ 
  invoiceId, 
  hospitalData = null, 
  invoiceData = null, 
  customerInfo = null,
  onInvoiceData = null,
  loading = false,
  disabled = false,
  autoTriggerCancellationPrint = false,
  autoOpen = false,
  hideTrigger = false,
  onViewerClose = null,
  // 'thermal' | 'a4' | 'a5': open the preview directly instead of asking
  printType = null
}) => {
  // Print type selection dialog states
  const [printTypeDialogOpen, setPrintTypeDialogOpen] = useState(false);
  const [selectedPrintType, setSelectedPrintType] = useState('thermal');
  
  // Invoice viewing states
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  const [processedInvoiceData, setProcessedInvoiceData] = useState(null);
  const [processedCustomerInfo, setProcessedCustomerInfo] = useState(null);
  const [isThermalPrint, setIsThermalPrint] = useState(true);
  const [isA5Print, setIsA5Print] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  
  const autoTriggerExecutedRef = useRef(false);
  const autoOpenExecutedRef = useRef(false);
  const handleViewInvoiceRef = useRef(null);
  
  useEffect(() => {
    if (autoTriggerCancellationPrint && invoiceData && customerInfo && !autoTriggerExecutedRef.current) {
      autoTriggerExecutedRef.current = true;
      
      setProcessedInvoiceData(invoiceData);
      setProcessedCustomerInfo(customerInfo);
      
      setSelectedPrintType('thermal');
      setIsThermalPrint(true);
      setIsA5Print(false);
      setOpenInvoiceModal(true);
    }
    
    if (!autoTriggerCancellationPrint) {
      autoTriggerExecutedRef.current = false;
    }
  }, [autoTriggerCancellationPrint, invoiceData, customerInfo]);

  const processInvoiceData = (invoice) => {
    const isPharmacyInvoice = invoice.type === 'pharmacy' || invoice.bill_type === 'pharmacy' || (invoice.items?.pharmacy && invoice.items.pharmacy.length > 0);
    const isLabInvoice = invoice.type === 'lab' || invoice.bill_type === 'lab' || (invoice.items?.lab_tests && invoice.items.lab_tests.length > 0);
    
    let processedData = null;
    
    if (isPharmacyInvoice) {
      const hospitalDetails = normalizeHospitalDetails(invoice?.hospital_details || invoice?.store || {});
      const pharmacyItems = (invoice?.items?.pharmacy || []).map((item) => ({
        ...item,
        gst_percentage: getItemGstPercentage(item)
      }));
      const activeItems = pharmacyItems.filter(item => !item.status || item.status === 'active');
      const normalizedInvoice = {
        ...invoice,
        items: { ...invoice?.items, pharmacy: pharmacyItems }
      };
      const subtotal = Number(invoice?.actualSubtotal ?? invoice?.original_amount ?? 0);
      const discountPercent = Number(invoice?.discount || 0);
      const computedLineDiscountTotal = activeItems.reduce((sum, item) => {
        const unit = Number(item.selling_price || item.price || 0);
        const qty = Number(item.quantity || 1);
        return sum + (unit * qty * Number(item.discount_in_perc || 0)) / 100;
      }, 0);
      const explicitActualDiscount =
        invoice?.actualDiscount != null && invoice?.actualDiscount !== ''
          ? Number(invoice.actualDiscount)
          : null;
      const totalDiscount = Number(
        computedLineDiscountTotal > 0
          ? computedLineDiscountTotal
          : (explicitActualDiscount !== null
              ? explicitActualDiscount
              : (subtotal > 0 ? (subtotal * discountPercent) / 100 : 0))
      );
      const pharmacyGstSummary = summarizeInclusiveGst({
        items: activeItems,
        gstEnabled: hospitalDetails.pharmacyGstEnabled,
        mode: 'cgst_sgst',
        summarySource: invoice
      });
      const resolvedTotals = resolvePharmacyInvoiceTotals({
        netTotal: pharmacyGstSummary.totalAmount
      });
      const isPositiveRoundOff = resolvedTotals.isPositiveRoundOff;
      const finalTotal = resolvedTotals.grandTotal;
      
      processedData = {
        ...normalizedInvoice,
        payment_method: invoice.payment_method,
        invoice_number: invoice?.invoice_number,
        created_at: invoice.created_at ? new Date(invoice.created_at) : new Date(),
        receptionist: invoice?.receptionist,
        actualSubtotal: subtotal,
        actualDiscount: totalDiscount,
        roundOff: resolvedTotals.roundOffDisplay,
        isPositiveRoundOff,
        finalTotal,
        total_amount: finalTotal,
        round_off: resolvedTotals.roundOff,
        store: invoice?.store || {},
        hospital_details: hospitalDetails,
        pharmacy_gst_summary: pharmacyGstSummary,
        store_name: invoice?.store?.name
      };
    } else if (isLabInvoice) {
      const rawRoundOff = Number(invoice?.round_off || 0);
      const isPositiveRoundOff = typeof invoice?.isPositiveRoundOff === 'boolean'
        ? invoice.isPositiveRoundOff
        : rawRoundOff > 0;
      const finalTotal = Number(invoice?.total_amount || 0);
      
      processedData = {
        ...invoice,
        actualSubtotal: Number(invoice?.actualSubtotal ?? invoice?.original_amount ?? 0),
        actualDiscount: Number(invoice?.actualDiscount ?? 0),
        roundOff: Math.abs(rawRoundOff),
        isPositiveRoundOff,
        finalTotal,
        total_amount: finalTotal
      };
    } else {
      const opdItems = invoice?.items?.opd_appointment || [];
      if (opdItems.length > 0) {
        const labTestsFormat = opdItems.map(item => ({
          ...item,
          price: item.selling_price,
          quantity: item.quantity || 1,
          discount_in_perc: item.discount_in_perc || '0.00',
          status: item.status || 'active'
        }));
        const normalizedInvoice = {
          ...invoice,
          items: { lab_tests: labTestsFormat }
        };
        const rawRoundOff = Number(invoice?.round_off || 0);
        const isPositiveRoundOff = typeof invoice?.isPositiveRoundOff === 'boolean'
          ? invoice.isPositiveRoundOff
          : rawRoundOff > 0;
        const finalTotal = Number(invoice?.total_amount || 0);
        
        processedData = {
          ...normalizedInvoice,
          items: { lab_tests: labTestsFormat },
          actualSubtotal: Number(invoice?.actualSubtotal ?? invoice?.original_amount ?? 0),
          actualDiscount: Number(invoice?.actualDiscount ?? 0),
          roundOff: Math.abs(rawRoundOff),
          isPositiveRoundOff,
          finalTotal,
          total_amount: finalTotal
        };
      }
    }
    
    return processedData;
  };

  const openPrintPreview = () => {
    if (!printType) {
      setPrintTypeDialogOpen(true);
      return;
    }
    setSelectedPrintType(printType);
    setIsThermalPrint(printType === 'thermal');
    setIsA5Print(printType === 'a5');
    setOpenInvoiceModal(true);
  };

  const handleViewInvoice = async () => {
    if (invoiceData && customerInfo) {
      setProcessedInvoiceData(processInvoiceData(invoiceData) || invoiceData);
      setProcessedCustomerInfo(customerInfo);
      openPrintPreview();
    } else if (invoiceId) {
      setLoadingInvoice(true);
      try {
        const response = await fetchInvoiceByInvoiceNumber(invoiceId);
        
        if (response.results && response.results.length > 0) {
          const invoice = response.results[0];
          
          const processedData = processInvoiceData(invoice);
          
          if (processedData) {
            setProcessedInvoiceData(processedData);
            setProcessedCustomerInfo(invoice.customer || {});
            openPrintPreview();
          } else {
            // Fallback: show the raw invoice data if type detection fails
            setProcessedInvoiceData(invoice);
            setProcessedCustomerInfo(invoice.customer || {});
            openPrintPreview();
          }
        } else {
          openSnackbar({
            open: true,
            message: "Invoice not found",
            variant: "alert",
            alert: { color: "error" }
          });
          onViewerClose?.();
        }
      } catch (error) {
        openSnackbar({
          open: true,
          message: "Failed to fetch invoice details",
          variant: "alert",
          alert: { color: "error" }
        });
        onViewerClose?.();
      } finally {
        setLoadingInvoice(false);
      }
    }
  };

  handleViewInvoiceRef.current = handleViewInvoice;

  useEffect(() => {
    if (autoOpen && !autoOpenExecutedRef.current) {
      autoOpenExecutedRef.current = true;
      handleViewInvoiceRef.current?.();
    }
    if (!autoOpen) autoOpenExecutedRef.current = false;
  }, [autoOpen]);

  const handlePrintTypeSelection = () => {
    if (processedInvoiceData && processedCustomerInfo !== null) {
      setIsThermalPrint(selectedPrintType === 'thermal');
      setIsA5Print(selectedPrintType === 'a5');
      setOpenInvoiceModal(true);
      
      // Call callback if provided
      if (onInvoiceData) {
        onInvoiceData(processedInvoiceData, processedCustomerInfo, selectedPrintType);
      }
    } else {
      openSnackbar({
        open: true,
        message: "Error: Invalid invoice data",
        variant: "alert",
        alert: { color: "error" }
      });
    }
    
    // Close the dialog and reset pending state
    setPrintTypeDialogOpen(false);
  };

  const handlePrintTypeCancel = () => {
    setPrintTypeDialogOpen(false);
    setProcessedInvoiceData(null);
    setProcessedCustomerInfo(null);
    onViewerClose?.();
  };

  const handleCloseInvoice = () => {
    setOpenInvoiceModal(false);
    setProcessedInvoiceData(null);
    setProcessedCustomerInfo(null);
    
    if (autoTriggerCancellationPrint && onInvoiceData) {
      onInvoiceData();
    }
    onViewerClose?.();
  };  

  return (
    <>
      {/* View Button */}
      {!hideTrigger && (
        <Tooltip title="View Original Invoice">
          <IconButton
            size="small"
            color="primary"
            disabled={loadingInvoice || disabled}
            onClick={handleViewInvoice}
            aria-label={`View ${invoiceId || invoiceData?.invoice_number || 'invoice'}`}
          >
            <VisibilityOutlinedIcon style={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Invoice Viewing Modal */}
      {processedInvoiceData && (
        <InvoicePrintPreviewModal
          isThermalPrint={isThermalPrint}
          isA5Print={isA5Print}
          open={openInvoiceModal}
          onClose={handleCloseInvoice}
          title={`Invoice_${processedInvoiceData?.invoice_number}`}
          reportId={processedInvoiceData?.invoice_number || processedInvoiceData?.id}
          patientId={processedCustomerInfo?.id}
          patientName={processedCustomerInfo?.full_name}
        >
          {processedInvoiceData?.items?.pharmacy && processedInvoiceData.items.pharmacy.length > 0 ? (
            isThermalPrint ? (
              <PharmacyInvoiceThermalContent customerInfo={processedCustomerInfo || {}} invoiceData={processedInvoiceData} hospitalData={hospitalData}/>
            ) : (
              <PharmacyInvoiceA4Content customerInfo={processedCustomerInfo || {}} invoiceData={processedInvoiceData} hospitalData={hospitalData}/>
            )
          ) : processedInvoiceData?.items?.opd_appointment && processedInvoiceData.items.opd_appointment.length > 0 ? (
            isThermalPrint ? (
              <LabInvoiceThermalContent 
                customerInfo={processedCustomerInfo || {}} 
                invoiceData={{
                  ...processedInvoiceData,
                  items: { lab_tests: processedInvoiceData.items.opd_appointment }
                }} 
              />
            ) : (
              <LabInvoiceA4Content 
                customerInfo={processedCustomerInfo || {}} 
                invoiceData={{
                  ...processedInvoiceData,
                  items: { lab_tests: processedInvoiceData.items.opd_appointment }
                }} 
              />
            )
          ) : (
            // Lab invoice or fallback (use lab templates)
            isThermalPrint ? (
              <LabInvoiceThermalContent customerInfo={processedCustomerInfo || {}} invoiceData={processedInvoiceData} />
            ) : (
              <LabInvoiceA4Content customerInfo={processedCustomerInfo || {}} invoiceData={processedInvoiceData} />
            )
          )}
        </InvoicePrintPreviewModal>
      )}

      {/* Print Type Selection Dialog */}
      <Dialog open={printTypeDialogOpen} onClose={handlePrintTypeCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Select Print Format</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Choose the format for viewing the invoice:
          </Typography>
          <RadioGroup
            value={selectedPrintType}
            onChange={(e) => setSelectedPrintType(e.target.value)}
          >
            <FormControlLabel
              value="thermal"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Thermal Print (Token Format)
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Compact receipt format suitable for thermal printers
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="a4"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    A4 Print (Full Format)
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Standard A4 paper format with detailed layout
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="a5"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    A5 Print
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    A5 paper format optimized for invoices, prevents content cutoff
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePrintTypeCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handlePrintTypeSelection} variant="contained" color="primary">
            View Invoice
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvoiceViewer;
